import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  colors,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Project, ReportProgressStatus } from "../../types/project";
import { Place } from "@mui/icons-material";
import { getHiredTalentStatus } from "../../utils/projectHelper";
import { formatWithTimezone } from "../../utils/dateHelper";
import { DataList, DataListColumn } from "../../components/global";

const TodaysProjects = ({
  data,
  isLoading,
  emptyText,
}: {
  data: Project[];
  isLoading: boolean;
  emptyText?: string;
}) => {
  const navigate = useNavigate();

  const sorted = React.useMemo(() => {
    let inProgress: Array<Project> = [];
    let scheduled: Array<Project> = [];
    let completion: Array<Project> = [];
    data?.forEach((job) => {
      const hiredList = job.resources?.filter((it) => !!it.hired_at) || [];
      hiredList.forEach((hired) => {
        if (
          hired.report_progresses?.length != undefined &&
          hired.report_progresses?.length > 0
        ) {
          let found = hired.report_progresses?.find((progress) => {
            if (
              progress.status === ReportProgressStatus.SUBMITTED ||
              progress.status === ReportProgressStatus.APPROVED
            ) {
              return progress;
            }
          });
          if (found) {
            completion.push(job);
          } else inProgress.push(job);
        } else {
          scheduled.push(job);
        }
      });
    });
    const merged = [...inProgress, ...scheduled];
    return merged;
  }, [data]);

  const columns: DataListColumn[] = [
    { label: "WORK TITLE", flex: 5, align: "left" },
    { label: "MANAGER NAME", flex: 2, align: "center" },
    { label: "INDEPENDENT PROFESSIONAL", flex: 2, align: "center" },
    { label: "STATUS", flex: 3, align: "center" },
  ];

  // Flatten the data structure for DataList
  const flattenedData = React.useMemo(() => {
    const items: Array<{
      job: Project;
      hiredTalent: any;
      jobIndex: number;
      talentIndex: number;
    }> = [];

    sorted?.forEach((job, jobIndex) => {
      const hiredTalents = job.resources?.filter(
        (resource) => !!resource.hired_at
      ) || [];
      hiredTalents.forEach((hiredTalent, talentIndex) => {
        items.push({ job, hiredTalent, jobIndex, talentIndex });
      });
    });

    return items;
  }, [sorted]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Box sx={{ minWidth: { xs: 650, sm: "100%" } }}>
        <DataList
          columns={columns}
          data={flattenedData}
          isLoading={isLoading}
          emptyText={emptyText ?? "No Projects"}
          maxHeight={600}
          showDivider={true}
          renderItem={(item, index) => {
            const { job, hiredTalent } = item;
            const status = getHiredTalentStatus(job, hiredTalent);

            // Get location text with better fallback handling
            let locationText = "N/A";
            if (job.customer) {
              const firstName = job.customer.first_name || "";
              const lastName = job.customer.last_name || "";
              locationText = `${firstName} ${lastName}`.trim() || "N/A";
            } else if (job.location) {
              locationText = job.location.name || job.location.site_number || "N/A";
            } else if (job.location_id) {
              // If location_id exists but location object is not loaded
              locationText = "Location not loaded";
            }

            const isChecked = status.label.startsWith("CHECKED");

            return (
              <Box
                key={`${job.id}-${hiredTalent.resource_id}-${item.talentIndex}-${item.jobIndex}`}
                onClick={() => navigate(`/works/${job.id}?t=1`)}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: isChecked
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 235, 59, 0.15)"
                      : "rgba(255, 235, 59, 0.12)"
                    : "transparent",
                  borderRadius: 2,
                  mb: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1, sm: 2.5 },
                  py: { xs: 1, sm: 1.75 },
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `1px solid ${isChecked
                    ? theme.palette.mode === "dark"
                      ? "rgba(255, 235, 59, 0.3)"
                      : "rgba(255, 235, 59, 0.25)"
                    : "transparent"
                    }`,
                  "&:hover": {
                    backgroundColor: isChecked
                      ? theme.palette.mode === "dark"
                        ? "rgba(255, 235, 59, 0.2)"
                        : "rgba(255, 235, 59, 0.18)"
                      : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                    transform: "translateX(6px)",
                    boxShadow: theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                      : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    borderColor: isChecked
                      ? theme.palette.mode === "dark"
                        ? "rgba(255, 235, 59, 0.4)"
                        : "rgba(255, 235, 59, 0.35)"
                      : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                {/* WORK TITLE Column - flex: 5, align: left */}
                <Box
                  sx={{
                    flex: { xs: 3, sm: 5 },
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body1"
                    className="text-primary-green"
                    sx={{
                      fontWeight: isChecked ? 700 : 600,
                      fontSize: { xs: "0.75rem", sm: "0.95rem" },
                      mb: { xs: 0.25, sm: 0.5 },
                      lineHeight: 1.4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {`#${job.id} - ${job.title}`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Place sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }} color="disabled" />
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.8rem" },
                        color: "text.secondary",
                        fontWeight: 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {locationText}
                    </Typography>
                  </Box>
                </Box>

                {/* MANAGER NAME Column - flex: 2, align: center */}
                <Box
                  sx={{
                    flex: { xs: 1.5, sm: 2 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", sm: "0.875rem" },
                      fontWeight: 500,
                      color: "text.primary",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {job.manager?.full_name || "N/A"}
                  </Typography>
                </Box>

                {/* INDEPENDENT PROFESSIONAL Column - flex: 2, align: center */}
                <Box
                  sx={{
                    flex: { xs: 1.5, sm: 2 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.65rem", sm: "0.875rem" },
                      fontWeight: 500,
                      color: "text.primary",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {hiredTalent.resource?.full_name || "N/A"}
                  </Typography>
                </Box>

                {/* STATUS Column - flex: 3, align: center */}
                <Box
                  sx={{
                    flex: { xs: 2, sm: 3 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <Chip
                    label={status.label}
                    color={status.color}
                    size="small"
                    sx={{
                      height: { xs: 20, sm: 24 },
                      fontSize: { xs: "0.6rem", sm: "0.75rem" },
                      fontWeight: 600,
                      mb: { xs: 0.25, sm: 0.75 },
                      boxShadow: theme.palette.mode === "dark"
                        ? "0 2px 4px rgba(0, 0, 0, 0.3)"
                        : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.75rem" },
                      color: "text.secondary",
                      fontWeight: 400,
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatWithTimezone(
                      job.start_date,
                      job.timezone,
                      isMobile ? "MMM D, h:mmA" : "ddd, MMM D YYYY - h:mmA z"
                    )}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};
export default TodaysProjects;
